import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type FeatureMockSize = 'compact' | 'showcase';

export interface FeatureMockFrameProps {
  id: string;
  alt: string;
  size?: FeatureMockSize;
  /** Show a small lock chip (default true when size is compact). */
  showLock?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Decorative product UI mock treated as a single image for a11y and SEO.
 * Inner content is aria-hidden; data-nosnippet discourages search snippet use.
 */
export function FeatureMockFrame({
  id,
  alt,
  size = 'compact',
  showLock,
  className,
  children,
}: FeatureMockFrameProps) {
  const lockChip = showLock ?? size === 'compact';

  return (
    <div
      role="img"
      aria-label={alt}
      data-nosnippet
      className={cn('csm', className)}
      data-mock={id}
      data-size={size}
    >
      {lockChip ? (
        <div className="csm-lock-chip" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
        </div>
      ) : null}
      <div aria-hidden="true" className="csm-stage">
        {children}
      </div>
    </div>
  );
}
